import { Box, Flex, Stack } from "@chakra-ui/react"
import * as React from "react"
import * as Yup from "yup"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { HomeDescription } from "./homeDescription"
import { SDKContext } from "../../../context/SDKContext"
import useReducedLandscapeView from "../../../hooks/useLandscapeViews"
import useQuery from "../../../hooks/useQuery"
import useWindowSize from "../../../hooks/useWindowSize"
import { joinButtonLoadingSelector } from "../../../recoil/atoms/joinButton"
import { brandingSelector, roomSelector } from "../../../recoil/atoms/room"
import { localUserNameAtom } from "../../../recoil/atoms/users"
import { HomeAvatar } from "./homeAvatar"
import { UserType } from "../../../types/user"
import { isMobileDevice } from "../../../util/browser"
import {
  getUserTypeByUrl,
  getUserTypeByUrlV2
} from "../../../util/getUserTypeByUrl"
import SettingsContainer from "../../settings/settingsContainer"
import { HomeForm } from "./homeForm"
import { useLocalStorage } from "../../../hooks/useLocalStorage"
import { v4 as uuidv4 } from "uuid"
import { AvatarsModal } from "./AvatarsModal/"
import { useBoolean } from "../../../hooks/useBoolean"
import { saveConfigAvatarAtom } from "../../../recoil/atoms/avatar"
import { useResizeObserver } from "../../../hooks/useResizeObserver"
import { useBrowserType } from "../../../hooks/useBrowserType"
import layoutStates from "../../../recoil/atoms/layout"
import { detectDeviceType } from "../../../util/deviceType"
import useWsReconnection from "../../../hooks/useWsReconnection"
import { Logo } from "../../header/logo"
import badWords from "../../../constants/badWords"
import { setPrivateRoomSelector } from "../../../recoil/atoms/privateRoom"
import { LanguageButton } from "./languages/language-button"
import { Languages } from "./languages/languages"
import { useTranslation } from "react-i18next"
import { joinErrorMessageAtom } from "../../../recoil/atoms/error"
import { CURRENT_URL } from "../../../constants"
import { showNoVideoSubscribeScreenSelector } from "../../../recoil/atoms/rtc"
import MOLocation from "../../../util/MOLocation"
import HomeBackIcon from "./HomeBackIcon"
import { useReturnToJourneyAction } from "../../../recoil/atoms/journeyUI"
import useConfig from "src/hooks/useConfig"
import useAuthedUser from "src/hooks/useAuthedUser"
import { SafeArea } from "src/components/common/SafeArea"

const _Home = () => {
  const { t } = useTranslation()
  const [typeModal, setTypeModal] = React.useState<"languages" | "avatar">(
    "languages"
  )
  const query = useQuery()
  const setSaveConfig = useSetRecoilState(saveConfigAvatarAtom)
  const device = detectDeviceType()
  const meetingClient = React.useContext(SDKContext)
  const showMinifiedLables = useReducedLandscapeView()
  const { screenSize, refreshScreenSize } = useWindowSize()
  const { isLandscape /* isSafariMobile  */ } = useBrowserType()
  const [showHeader] = useRecoilState(layoutStates.showHeader)
  const [localUserAvatarStorage, localUserAavatarParseStorage] =
    useLocalStorage("localUserAavatar")
  const homeInputRef = React.useRef<HTMLInputElement>(null)

  const isLoadingScreenSize = screenSize.width === 0
  const isMobile =
    !isLoadingScreenSize &&
    (isMobileDevice(screenSize) ||
      (isLandscape && !(device === "IOSChrome" && screenSize.width >= 1024)))

  const isAndroidMobile = React.useMemo(() => {
    return device === "Android" && isMobileDevice(screenSize)
  }, [device, screenSize])

  const isSmallWidth = React.useMemo(() => {
    return screenSize.width < 415
  }, [screenSize.width])

  const [isOpen, { off, on }] = useBoolean(false)
  const setCurrentUserName = useSetRecoilState(localUserNameAtom)
  const [, setShowHeader] = useRecoilState(layoutStates.showHeader)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [, height] = useResizeObserver(containerRef)
  const [localUserNameStorage, localUserNameParseStorage, setLocalUserName] =
    useLocalStorage("localUserNameLogin")
  const [
    localLanguageStorage,
    localLanguageParseStorage,
    setLocalUserLanguage
  ] = useLocalStorage("localLanguage")
  const [error, setError] = React.useState<string>("")
  const [errorWhileJoinMeeting, setErrorWhileJoinMeeting] =
    useRecoilState(joinErrorMessageAtom)
  const schema = Yup.string()
    .max(1024, "max. 1024 char")
    .required(t("A screen name is required"))
    .test("nospace", "Text is empty", function (value) {
      const text = value.split("")
      const haveSpace = element => element !== " "
      return text.some(haveSpace)
    })
    .test(
      "Ascreennameisrequired",
      t("A screen name is required"),
      function (value) {
        return value !== "" && value !== " "
      }
    )

  const { isPrivateRoom, privateRoomData } = useRecoilValue(
    setPrivateRoomSelector
  )
  const [name, setName] = React.useState<string>("")
  const { i18n } = useTranslation()
  useWsReconnection()

  React.useEffect(() => {
    refreshScreenSize()
  }, [])

  React.useEffect(() => {
    if (localLanguageStorage) {
      const values = Object.values(localLanguageParseStorage) as {
        language: string
        url: string
      }[]

      values.forEach(element => {
        if (element.url === CURRENT_URL) {
          i18n.changeLanguage(element.language)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (localUserNameStorage && !isPrivateRoom) {
      const values = Object.values(localUserNameParseStorage) as {
        nameUser: string
        url: string
      }[]

      values.forEach(element => {
        if (element.url === CURRENT_URL) {
          setCurrentUserName(element.nameUser)
          setName(`${element.nameUser}`)
        }
      })
    }

    if (isPrivateRoom) {
      setName(privateRoomData?.userName)
    }

    if (localUserAvatarStorage) {
      const values = Object.values(localUserAavatarParseStorage) as {
        avatarConfig: string
        url: string
      }[]

      values.forEach(element => {
        if (element.url === CURRENT_URL) {
          setSaveConfig(element.avatarConfig)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [visible, setVisible] = React.useState<boolean>(false)
  const [loading, setLoading] = useRecoilState(joinButtonLoadingSelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const room = useRecoilValue(roomSelector)
  const config = useRecoilValue(saveConfigAvatarAtom)
  const isMediaDisabled = useRecoilValue(showNoVideoSubscribeScreenSelector)

  const userTypeFromUrl = getUserTypeByUrlV2()
  const profileImageUrl = roomStyles?.roomAvatarImage ?? ""
  const isModeratorType = React.useMemo(
    () => userTypeFromUrl === UserType.MODERATOR,
    [userTypeFromUrl]
  )

  const isAudienceType = React.useMemo(
    () => userTypeFromUrl === UserType.AUDIENCE,
    [userTypeFromUrl]
  )

  const isMicCamRequired =
    (!isAudienceType || isPrivateRoom) && !isMediaDisabled

  const { updateAuthedUser } = useAuthedUser()

  React.useEffect(() => {
    if (room?.enableHeaderBar === true) {
      setShowHeader(true)
    }
    if (room?.enableHeaderBar === false) {
      setShowHeader(false)
    }
  }, [room?.enableHeaderBar, setShowHeader])

  const joinMeeting = React.useCallback(async () => {
    updateAuthedUser({ screenName: name })
    try {
      setErrorWhileJoinMeeting(null)
      if (meetingClient) {
        const nameWithoutSpace = name.trim()
        const userName = await schema.validate(nameWithoutSpace)

        if (userName.length > 24) {
          setError(t("Max. 24 characters"))
          return
        }

        const isBadWordContent = badWords.find(word =>
          userName.toLowerCase().includes(word.toLowerCase())
        )
        if (isBadWordContent) {
          setError(t("Not Permitted"))
          return
        }

        const canOpenFullscreenIframeMeeting =
          MOLocation.getQueryParams().iframe
        if (canOpenFullscreenIframeMeeting) {
          meetingClient.parentPageCommunication.openMeetingOnFullscreenIframe({
            userName,
            avatar: config
          })
          return
        }

        setLoading(true)
        meetingClient?.setUserName(userName)
        if (config !== "") {
          meetingClient?.setUserAvatar(config)
        }
        if (config === "" && isModeratorType) {
          meetingClient?.setUserAvatar(profileImageUrl)
        }

        if (!isMicCamRequired) {
          meetingClient?.setJoinPageMicEnabled(false)
          meetingClient?.setJoinPageCamEnabled(false)
        }

        setError("")
        meetingClient?.connectGateway()

        setLocalUserName(
          {
            [`user-${uuidv4()}`]: {
              nameUser: userName,
              url: CURRENT_URL
            }
          },
          "add"
        )
      }
    } catch (e) {
      setError(e.message)
    }
  }, [
    t,
    config,
    isMicCamRequired,
    isModeratorType,
    meetingClient,
    name,
    profileImageUrl,
    schema,
    setErrorWhileJoinMeeting,
    setLoading,
    setLocalUserName
  ])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || (isMobile && event.key === "return")) {
      if (isMobile) {
        homeInputRef.current.blur()
      }
      joinMeeting()
    }
  }

  React.useEffect(() => {
    if (query.userName) {
      setName(query.userName.toString())
    }
  }, [query.userName])

  const isDifferentTypeAndDesktop = React.useMemo(
    () => isMicCamRequired && !isMobile && !isLoadingScreenSize,
    [isMicCamRequired, isMobile, isLoadingScreenSize]
  )

  const wrapperStyles: React.CSSProperties = React.useMemo(() => {
    return {
      overflow: "auto",
      backgroundColor: roomStyles?.joinPageBackgroundColor ?? "gray.100",
      backgroundImage: roomStyles?.joinPageBackgroundImage
        ? `url(${roomStyles.joinPageBackgroundImage})`
        : "none",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      left: "0",
      // top: "64px",
      width: "100%",
      height: "100%",
      textAlign: "center",
      position: "relative"
    }
  }, [roomStyles?.joinPageBackgroundImage, roomStyles?.joinPageBackgroundColor])

  const containerStyles: React.CSSProperties = React.useMemo(() => {
    return {
      background: isOpen
        ? roomStyles?.joinPagePanelBackgroundColor ?? "white"
        : roomStyles?.joinPagePanelBackgroundColor ?? "#FFFFFF",
      borderColor: roomStyles?.joinPagePanelBorderColor ?? "transparent",
      borderWidth: 0,
      maxWidth:
        // isLandscape && isMobile && isMicCamRequired
        //   ? "500px"
        //   :
        isOpen && !isMobile && typeModal === "avatar"
          ? "100%"
          : isMobile
          ? "400px"
          : !isMicCamRequired
          ? "470px"
          : "unset",
      width: "100%",
      margin: "0 auto"
    }
  }, [
    isOpen,
    roomStyles?.joinPagePanelBackgroundColor,
    roomStyles?.joinPagePanelBorderColor,
    // isLandscape,
    isMobile,
    isMicCamRequired,
    typeModal
  ])

  const [animation, setAnimation] = React.useState(false)

  const handleOpenAvatarModal = () => {
    setTypeModal("avatar")
    on()

    if (isAudienceType) {
      setTimeout(() => {
        setVisible(true)
      }, 500)
    }
  }

  const handleCloseModal = () => {
    off()
    setAnimation(true)
    setVisible(false)
  }

  React.useEffect(() => {
    if (animation) {
      let timer = setTimeout(() => {
        setAnimation(false)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [animation])

  const isPortraitMobileAvatarView = React.useMemo(() => {
    return isOpen && isMobile && isMicCamRequired
  }, [isMicCamRequired, isMobile, isOpen])

  const clearErrorMessage = React.useCallback(() => {
    setError("")
    setErrorWhileJoinMeeting(null)
  }, [setError, setErrorWhileJoinMeeting])

  const isSmallContainer = React.useMemo(() => {
    return !isMicCamRequired && (!isMobile || isLandscape) && !isOpen
  }, [isMicCamRequired, isLandscape, isMobile, isOpen])

  useReturnToJourneyAction()

  const { localCamEnabled, localMuteEnabled, uttaAuthPage } = useConfig()
  const camEnabled = localCamEnabled === true

  const safeAreaStyle = {
    backgroundColor: roomStyles?.joinPageBackgroundColor ?? "gray.100",
    backgroundImage: roomStyles?.joinPageBackgroundImage
      ? `url(${roomStyles.joinPageBackgroundImage})`
      : "none"
  }

  return (
    <SafeArea {...safeAreaStyle}>
      <Box
        style={wrapperStyles}
        _before={{
          content: "''",
          display: "inline-block",
          verticalAlign: "middle",
          height: "100%"
        }}
      >
        <HomeBackIcon navigatedFromiOS={false} />
        <Box
          spacing={8}
          mx={"auto"}
          maxW={isSmallContainer || isLandscape ? "470px" : "960px"}
          w={!isSmallContainer && "100%"}
          py={isLandscape ? "10px" : isMobile && isModeratorType ? 6 : 12}
          px={!isSmallContainer && 6}
          display="inline-block"
          verticalAlign="middle"
        >
          <Flex
            direction="column"
            style={containerStyles}
            pos="relative"
            rounded={"lg"}
            boxShadow={"lg"}
            py={4}
            px={4}
            margin="0 auto"
            width="100%"
            maxWidth={
              isLandscape && isMobile && isMicCamRequired
                ? "500px"
                : (isMobile || !isMicCamRequired) && "400px"
            }
            borderWidth="1px"
            transitionDuration={!isMicCamRequired && isOpen && "500ms"}
            borderColor={roomStyles?.joinPagePanelBorderColor ?? "transparent"}
            ref={containerRef}
            // set height for avatar and language panel
            height={isOpen && isMobile && isLandscape && "fit-content"}
            maxHeight={
              typeModal === "languages" && isOpen
                ? 412
                : isOpen &&
                  isMicCamRequired &&
                  isMobile &&
                  typeModal === "avatar" &&
                  "fit-content"
            }
          >
            {/* For Mobile */}
            {!isLandscape && isMobile && !(!isMicCamRequired && visible) && (
              <>
                {!isOpen && (
                  <>
                    {/* For mobile */}
                    {/* Add padding if below logo not shown */}
                    <Box
                    // paddingBottom={!(!showHeader && !isMicCamRequired) && "3"}
                    >
                      <LanguageButton
                        isMobile={isMobile}
                        toggleModal={() => {
                          setTypeModal("languages")
                          on()
                        }}
                      />
                    </Box>
                    {!showHeader && !isMicCamRequired && (
                      <Flex alignItems="center" justifyContent="center">
                        <Logo src={roomStyles?.roomHeaderIcon} />
                      </Flex>
                    )}
                  </>
                )}
              </>
            )}
            {isMobile &&
              isMicCamRequired &&
              !isLandscape &&
              localCamEnabled &&
              localMuteEnabled && (
                <Flex
                  width="100%"
                  marginRight={isLandscape && "10px"}
                  marginBottom={!isLandscape && "10px"}
                  visibility={isPortraitMobileAvatarView ? "hidden" : "unset"}
                  display={isOpen && "none"}
                >
                  <SettingsContainer
                    isHomePage={true}
                    isShowFullView={false}
                    isMobile={true}
                  />
                </Flex>
              )}
            {isOpen && (
              <>
                {/* <Flex width="100%" pos="relative"> */}
                {typeModal === "avatar" ? (
                  <AvatarsModal
                    isMobile={isMobile}
                    isAudienceType={isAudienceType && !isPrivateRoom}
                    closeModal={handleCloseModal}
                    isPortraitMobileAvatarView={isPortraitMobileAvatarView}
                    height={isMicCamRequired && height}
                    visible={visible}
                    isLandscape={isLandscape}
                  />
                ) : (
                  <Languages
                    isMobile={isMobile}
                    isAudienceType={isAudienceType && !isPrivateRoom}
                    height={isLandscape ? 300 : !isMicCamRequired ? 375 : 412}
                    visible={visible}
                    handleClose={handleCloseModal}
                    setLocalUserLanguage={setLocalUserLanguage}
                    showHeader={showHeader}
                  />
                )}
                {/* </Flex> */}
              </>
            )}

            {!(isMobile && !isLandscape) && !isOpen && (
              <LanguageButton
                toggleModal={() => {
                  setTypeModal("languages")
                  on()
                }}
                right={2}
                top={2}
                zIndex={2}
              />
            )}

            {(isLandscape || (!isMobile && !showHeader)) &&
              !isOpen &&
              !uttaAuthPage && (
                <Flex alignItems="center" justifyContent="center">
                  <Logo
                    src={roomStyles?.roomHeaderIcon}
                    isLandscape={isLandscape}
                  />
                </Flex>
              )}
            <Flex
              alignItems={!camEnabled ? "flex-end" : "center"}
              width="100%"
              // ref={containerRef}
              className={animation ? "setting-modal" : null}
              display={isOpen ? "none" : null}
              pos={
                isOpen &&
                !isDifferentTypeAndDesktop &&
                !isPortraitMobileAvatarView
                  ? "absolute"
                  : "relative"
              }
            >
              {isDifferentTypeAndDesktop && (
                <Flex w="50%" paddingRight="0.5rem">
                  <SettingsContainer isShowFullView={false} isHomePage={true} />
                </Flex>
              )}
              <Stack
                spacing={4}
                paddingLeft={isDifferentTypeAndDesktop && "0.5rem"}
                flex="1 0 50%"
              >
                <HomeDescription
                  roomStyles={roomStyles}
                  isLandscape={isLandscape}
                  showMinifiedLables={showMinifiedLables}
                  isSmallWidth={isSmallWidth}
                />

                <HomeAvatar
                  isMobile={isMobile}
                  showMinifiedLables={true}
                  openAvatarModal={
                    isAudienceType
                      ? handleOpenAvatarModal
                      : () => {
                          setTypeModal("avatar")
                          on()
                        }
                  }
                  isLandscape={isLandscape}
                  isAndroidMobile={isAndroidMobile}
                />
                <HomeForm
                  error={errorWhileJoinMeeting?.message ?? error}
                  isLandscape={isLandscape}
                  isSmallContainer={isSmallContainer}
                  clearError={clearErrorMessage}
                  homeInputRef={homeInputRef}
                  name={name}
                  setName={value => {
                    setName(value)
                  }}
                  disabledInputField={isPrivateRoom || !!query.userName}
                  handleKeyDown={handleKeyDown}
                  loading={loading}
                  isMobile={isMobile}
                  joinMeeting={joinMeeting}
                  isMicCamRequired={isMicCamRequired}
                />
              </Stack>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </SafeArea>
  )
}
export const Home = React.memo(_Home)
